<script setup>
import { computed, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { conversaDeTeste } from 'dashboard/helper/conversaDeTeste';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);

// Outlook-like selection: once any conversation is selected, reveal the
// checkbox on every card so the user can quickly tick more.
const selectedConversationIds = useMapGetter(
  'bulkActions/getSelectedConversationIds'
);
const isSelectionActive = computed(
  () => selectedConversationIds.value.length > 0
);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return (
    props.showInboxName ||
    (props.showAssignee && props.assignee.name) ||
    props.chat.priority
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const displayContactName = computed(() => {
  return (
    props.currentContact.display_name ||
    props.currentContact.name ||
    props.currentContact.phone_number ||
    props.currentContact.email ||
    props.currentContact.identifier ||
    'Visitante'
  );
});

// Subtitle shown below the contact name (BRIDGE-07-LAYOUT).
// Priority: phone_number > email > identifier (only if not a UUID/empty).
// Dedupe: skip if the candidate equals the contact name (degenerate case).
const displayContactSubtitle = computed(() => {
  const c = props.currentContact;
  if (!c) return null;
  const phone = c.phone_number?.toString().trim();
  const email = c.email?.toString().trim();
  const name = (c.display_name || c.name || '').toString().trim();
  const id = c.identifier?.toString().trim();
  const isUuid = id && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id);
  const usefulId = id && !isUuid && id !== name;
  const candidate = phone || email || (usefulId ? id : null);
  if (!candidate || candidate === name) return null;
  return candidate;
});

// A etiqueta "teste" (REPORTS-04 D29), pela régua dos relatórios.
const deTeste = computed(() =>
  conversaDeTeste(props.chat.labels, props.currentContact)
);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-start gap-3 cursor-pointer conversation border-b border-n-slate-3 hover:border-n-card hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-card before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select bg-pz-brand-tint hover:!bg-pz-brand-tint !border-n-card':
        isActiveChat,
      'selected bg-n-slate-2 !border-n-card': selected,
      'px-0': compact,
      'px-4': !compact,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      class="relative flex-shrink-0 mt-3"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="displayContactName"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected || isSelectionActive"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="py-3 flex-1 min-w-0 border-line">
      <div class="flex items-center min-w-0 gap-1.5 pr-12">
        <h4
          class="conversation--user min-w-0 text-sm my-0 capitalize text-ellipsis overflow-hidden whitespace-nowrap text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ displayContactName }}
        </h4>
        <Label
          v-if="deTeste"
          :label="{
            title: $t('CONVERSATION.TEST_CONVERSATION.LABEL'),
            description: $t('CONVERSATION.TEST_CONVERSATION.TOOLTIP'),
          }"
          color="amber"
          compact
        />
      </div>
      <p
        v-if="displayContactSubtitle"
        class="text-xs text-n-slate-11 truncate mt-0.5 pr-12"
      >
        {{ displayContactSubtitle }}
      </p>
      <div v-if="showMetaSection" class="flex flex-col min-w-0 gap-1 mt-1">
        <div class="flex items-center min-w-0 gap-2">
          <InboxName v-if="showInboxName" :inbox="inbox" class="min-w-0" />
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center truncate"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assignee.name }}
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
          <CardLabels
            v-if="showLabelsSection"
            :conversation-labels="chat.labels"
            class="min-w-0 mb-0"
          >
            <template v-if="hasSlaPolicyId" #before>
              <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
            </template>
          </CardLabels>
        </div>
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          :message-preview-class="messagePreviewClass"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="my-0 leading-5 w-full min-w-0 text-sm"
          :class="messagePreviewClass"
        />
        <p
          v-else
          key="no-messages"
          class="text-n-slate-11 text-sm my-0 leading-5 w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
          :class="messagePreviewClass"
        >
          <fluent-icon
            size="16"
            class="-mt-0.5 align-middle inline-block text-n-slate-10"
            icon="info"
          />
          <span class="mx-0.5">
            {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
          </span>
        </p>
      </div>
    </div>
    <div class="absolute flex flex-col ltr:right-3 rtl:left-3 top-3">
      <span class="ml-auto font-normal leading-4 text-xxs">
        <TimeAgo
          :last-activity-timestamp="chat.timestamp"
          :created-at-timestamp="chat.created_at"
          :conversation-id="chat.id"
        />
      </span>
      <UnreadBadge
        v-if="hasUnread"
        :count="unreadCount"
        class="ltr:ml-auto rtl:mr-auto mt-1"
      />
    </div>
  </div>
</template>
